import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint, debounce } from '@theme/utilities';

/**
 * @returns {boolean}
 */
function isRebuyAppPresent() {
  return Boolean(/** @type {{ Rebuy?: unknown }} */ (window).Rebuy);
}

/**
 * Timed fallback after anticipatory mini-cart suppress.
 *
 * Why a hard timeout (not ideal, but required):
 * - ATC must suppress the drawer immediately or mini-cart races the Post-ATC popup.
 * - Rebuy exposes `rebuy.show` / `rebuy.hide`, but no event for "no popup this ATC".
 * - Without a timeout, suppress never clears on products that skip Post-ATC (or after
 *   combined-listing switches onto those products) → mini-cart stays stuck closed.
 */
const REBUY_POPUP_FALLBACK_MS = 1000;
/** Same fallback on mobile; longer because Rebuy `show` is often slower on cellular. */
const REBUY_POPUP_FALLBACK_MOBILE_MS = 3000;

/**
 * A custom element that manages a cart drawer.
 *
 * Rebuy Post-ATC + mini-cart coordination:
 * - Buy-button click sets anticipatory suppress so mini-cart does not race the popup.
 * - We cannot only suppress on `rebuy.show`: CartAdd often opens the drawer first.
 * - We cannot leave suppress on forever: products without a Post-ATC offer never fire
 *   `rebuy.show` / `rebuy.hide`, so the flag would stick (esp. after combined-listing
 *   switches). Fall back to opening mini-cart if show never arrives.
 * - `rebuy.init` after morph clears leftover suppress from the previous product.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.3;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #rebuyPopupVisible = false;

  /** @type {boolean} */
  #openDrawerAfterRebuyHide = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #noPopupFallbackTimer = null;

  /** @type {((event: Event) => void) | null} */
  #noPopupShowHandler = null;

  /** @type {AbortController | null} */
  #installmentsCloseAbort = null;

  #debouncedStickyStateUpdate = debounce(() => {
    if (this.refs.dialog?.open) this.#updateStickyState();
  }, 300);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartSectionChange);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartSectionChange);
    document.addEventListener('rebuy.add', this.#handleRebuyAdd);
    document.addEventListener('rebuy.show', this.#handleRebuyShow);
    document.addEventListener('rebuy.hide', this.#handleRebuyHide);
    document.addEventListener('rebuy.ready', this.#handleRebuyReady);
    document.addEventListener('rebuy.init', this.#handleRebuyInit);
    window.addEventListener('resize', this.#debouncedStickyStateUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleDeferredCartWidgets);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // rebuy.ready may have fired before this element connected (common on mobile).
    if (document.body.hasAttribute('data-rebuy-pdp-ready') || isRebuyAppPresent()) {
      this.#handleRebuyReady();
    }

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelNoPopupFallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartSectionChange);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartSectionChange);
    document.removeEventListener('rebuy.add', this.#handleRebuyAdd);
    document.removeEventListener('rebuy.show', this.#handleRebuyShow);
    document.removeEventListener('rebuy.hide', this.#handleRebuyHide);
    document.removeEventListener('rebuy.ready', this.#handleRebuyReady);
    document.removeEventListener('rebuy.init', this.#handleRebuyInit);
    window.removeEventListener('resize', this.#debouncedStickyStateUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleDeferredCartWidgets);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#installmentsCloseAbort?.abort();
    this.#installmentsCloseAbort = null;
    this.#historyAbortController?.abort();
  }

  /**
   * Mount Shop Pay / wallets / installments after the drawer has painted so open INP
   * is not competing with accelerated-checkout custom element upgrades.
   */
  #scheduleDeferredCartWidgets = () => {
    requestAnimationFrame(() => {
      const mount = () => {
        this.#mountDeferredCartWidgets();
        // Rebind on every open: shadow CTA is async and must survive remounts / re-opens.
        void this.#bindInstallmentsCloseHandler();
      };

      const idle = /** @type {Window & { requestIdleCallback?: Function }} */ (window).requestIdleCallback;
      if (typeof idle === 'function') {
        idle.call(window, mount, { timeout: 200 });
      } else {
        setTimeout(mount, 0);
      }
    });
  };

  #mountDeferredCartWidgets() {
    const mounts = this.querySelectorAll('[data-cart-widget-mount]:not([data-mounted])');

    for (const root of mounts) {
      if (!(root instanceof HTMLElement)) continue;
      const tmpl = root.querySelector(':scope > template[data-cart-widget-template]');
      if (!(tmpl instanceof HTMLTemplateElement)) continue;

      root.appendChild(tmpl.content.cloneNode(true));
      tmpl.remove();
      root.dataset.mounted = 'true';
    }
  }

  /**
   * Close the drawer when installments CTA is clicked (avoids overlapping dialogs).
   * Waits for shopify-payment-terms shadow content; aborts prior binds on re-open.
   */
  async #bindInstallmentsCloseHandler() {
    this.#installmentsCloseAbort?.abort();
    this.#installmentsCloseAbort = new AbortController();
    const { signal } = this.#installmentsCloseAbort;

    // Empty cart / installments off: nothing to bind (avoid polling when CE is defined on PDP).
    if (!this.querySelector('shopify-payment-terms')) return;

    try {
      await customElements.whenDefined('shopify-payment-terms');
    } catch {
      return;
    }

    if (signal.aborted) return;

    const deadline = performance.now() + 3000;

    while (!signal.aborted && performance.now() < deadline) {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector(
        '#shopify-installments-cta'
      );

      if (cta instanceof Element) {
        // once: avoid double-close / double history.back() on mobile; signal: drop on re-open.
        cta.addEventListener('click', this.closeDialog, { once: true, signal });
        return;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  }

  #isRebuyDrawerSuppressed() {
    return this.hasAttribute('rebuy-pdp-widget-complete') && this.getAttribute('rebuy-pdp-widget-complete') !== 'true';
  }

  #suppressDrawerForRebuyPopup() {
    this.setAttribute('rebuy-pdp-widget-complete', 'false');
  }

  #clearRebuyDrawerSuppression() {
    this.removeAttribute('rebuy-pdp-widget-complete');
  }

  #cancelNoPopupFallback() {
    if (this.#noPopupFallbackTimer != null) {
      clearTimeout(this.#noPopupFallbackTimer);
      this.#noPopupFallbackTimer = null;
    }
    if (this.#noPopupShowHandler) {
      document.removeEventListener('rebuy.show', this.#noPopupShowHandler);
      this.#noPopupShowHandler = null;
    }
  }

  /**
   * After anticipatory suppress: wait for `rebuy.show`, else open mini-cart.
   * Hard timeout is required because Rebuy never signals "no upsell this time"
   * (see REBUY_POPUP_FALLBACK_* comment above). Cancels if `show` arrives first.
   */
  #scheduleDrawerIfNoRebuyPopup() {
    this.#cancelNoPopupFallback();

    this.#noPopupShowHandler = () => {
      this.#cancelNoPopupFallback();
    };
    document.addEventListener('rebuy.show', this.#noPopupShowHandler, { once: true });

    const fallbackMs = isMobileBreakpoint() ? REBUY_POPUP_FALLBACK_MOBILE_MS : REBUY_POPUP_FALLBACK_MS;

    this.#noPopupFallbackTimer = setTimeout(() => {
      this.#noPopupFallbackTimer = null;
      if (this.#noPopupShowHandler) {
        document.removeEventListener('rebuy.show', this.#noPopupShowHandler);
        this.#noPopupShowHandler = null;
      }
      if (this.#rebuyPopupVisible) return;

      this.#clearRebuyDrawerSuppression();
      if (this.hasAttribute('auto-open') && !this.refs.dialog?.open) {
        requestAnimationFrame(() => this.showDialog());
      }
    }, fallbackMs);
  }

  #handleRebuyInit = () => {
    // Morph remount dispatches `rebuy.init`. Clear suppress from the previous PDP so a
    // non-upsell product after a combined-listing switch can open mini-cart again.
    if (this.#rebuyPopupVisible) return;
    this.#cancelNoPopupFallback();
    this.#openDrawerAfterRebuyHide = false;
    this.#clearRebuyDrawerSuppression();
  };

  #handleRebuyShow = () => {
    this.#cancelNoPopupFallback();
    this.#rebuyPopupVisible = true;
    this.#openDrawerAfterRebuyHide = false;
    this.#suppressDrawerForRebuyPopup();

    // Mobile race: mini-cart may already be open — yield to Rebuy.
    if (this.refs.dialog?.open) {
      void this.closeDialog();
    }
  };

  #handleRebuyHide = () => {
    this.#rebuyPopupVisible = false;
    this.#clearRebuyDrawerSuppression();

    if (!this.#openDrawerAfterRebuyHide) return;
    this.#openDrawerAfterRebuyHide = false;
    requestAnimationFrame(() => this.showDialog());
  };

  #handleRebuyReady = () => {
    document.body.setAttribute('data-rebuy-pdp-ready', 'true');
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const target = customEvent.target;
    const isFromRebuyMount = target instanceof Element && target.closest('[data-rebuy-mount]') != null;
    const source = customEvent.detail?.data?.source;
    const isProductFormAdd = source === 'product-form-component';
    const rebuyReady = document.body.hasAttribute('data-rebuy-pdp-ready') || isRebuyAppPresent();
    const isPdp = document.querySelector('.product-information') != null;

    if (this.#rebuyPopupVisible) return;

    // Mobile often fires CartAdd before the buy-button click listener sets suppress
    // (touch / delayed click). Treat main PDP ATC with Rebuy present like anticipatory
    // suppress — not quick-add / other product-forms that share the page DOM.
    const isMainPdpBuyForm =
      isProductFormAdd &&
      target instanceof Element &&
      Boolean(target.closest('.product-information .buy-buttons-block')) &&
      !target.closest('quick-add-dialog');

    if (!this.#isRebuyDrawerSuppressed() && isMainPdpBuyForm && rebuyReady && isPdp && !isFromRebuyMount) {
      this.#suppressDrawerForRebuyPopup();
      this.#scheduleDrawerIfNoRebuyPopup();
      return;
    }

    if (this.#isRebuyDrawerSuppressed()) {
      this.#scheduleDrawerIfNoRebuyPopup();
      return;
    }

    if (this.hasAttribute('auto-open') || isFromRebuyMount) {
      requestAnimationFrame(() => this.showDialog());
    }
  };

  /**
   * @param {Event} [event]
   */
  open(event) {
    this.showDialog(event);
  }

  close() {
    this.closeDialog();
  }

  #handleCartSectionChange = () => {
    if (!this.refs.dialog?.open) return;

    queueMicrotask(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.#updateStickyState();
          // Section morph can restore deferred templates; remount while drawer stays open.
          this.#scheduleDeferredCartWidgets();
        })
      );
    });
  };

  #handleRebuyAdd = () => {
    // Only defer mini-cart until hide when the shopper added from inside an open popup.
    // PDP ATC can also emit rebuy.add while anticipatory suppress is on — must not set
    // openDrawerAfterRebuyHide or dismissing the popup auto-opens the drawer (esp. mobile).
    if (this.#rebuyPopupVisible) {
      this.#openDrawerAfterRebuyHide = true;
      void this.#refreshCartFromRebuy();
      return;
    }

    if (this.#isRebuyDrawerSuppressed()) {
      void this.#refreshCartFromRebuy();
      return;
    }

    requestAnimationFrame(() => this.showDialog());
    void this.#refreshCartFromRebuy();
  };

  async #refreshCartFromRebuy() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

    try {
      const [cart, sectionsData] = await Promise.all([
        fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json()),
        sectionId
          ? fetch(`${window.location.pathname}?sections=${sectionId}`).then((res) => res.json())
          : Promise.resolve(null),
      ]);

      const itemCount = cart?.item_count ?? 0;
      const sectionHtml = sectionId ? sectionsData?.[sectionId] : null;
      const sections = sectionId && sectionHtml ? { [sectionId]: sectionHtml } : {};
      document.dispatchEvent(new CartUpdateEvent({}, 'rebuy-widget', { source: 'rebuy', itemCount, sections }));
    } catch {
      // Silently fail - drawer content will refresh on next open anyway
    }
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    const sticky = ratio > this.#summaryThreshold ? 'false' : 'true';
    dialog.setAttribute('cart-summary-sticky', sticky);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
